/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Pagination, SearchParams, SearchResponse } from "./properties-types";

/**
 * Convert array of properties to object keyed by id
 */
export function arrayToObjectById(properties: any[]): Record<string, any> {
  const result: Record<string, any> = {};
  properties.forEach((prop) => {
    if (prop.id) {
      result[prop.id] = prop;
    }
  });
  return result;
}

/**
 * Build the `_page`/`_limit` query parameters from a pagination object.
 *
 * A parameter that is omitted (undefined/null) is skipped. A parameter that is
 * present MUST be a finite integer greater than zero — zero, negative, or
 * non-numeric values throw rather than being silently dropped or forwarded as
 * garbage to the backend.
 *
 * @param pagination - Object that may contain `page` and/or `limit`.
 * @returns A query object with stringified `_page`/`_limit` entries.
 * @throws {Error} If a provided `page` or `limit` is not a positive integer.
 */
export function buildPaginationQuery(pagination: Pagination): Record<string, string> {
  const query: Record<string, string> = {};
  assignPositiveParam(query, "_page", pagination.page);
  assignPositiveParam(query, "_limit", pagination.limit);
  return query;
}

/**
 * Validate a single pagination value and assign it to the query object.
 *
 * @param query - The query object being populated.
 * @param key - The target query key (`_page` or `_limit`).
 * @param value - The raw pagination value, possibly undefined.
 * @throws {Error} If the value is present but not a positive integer.
 */
function isEmptyParam(value: string | number | undefined | null): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Parses a pagination value into a positive integer, rejecting anything that is
 * not a whole number >= 1.
 *
 * @param key - The parameter name used in the error message (e.g. `_page`)
 * @param value - The raw value to coerce
 * @returns The parsed positive integer
 * @throws {Error} If the value is not a positive integer
 */
function toPositiveInteger(key: string, value: string | number): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new Error(`${key} must be a positive integer, received "${value}"`);
  }
  return parsed;
}

/**
 * Assigns a validated positive-integer pagination parameter onto the query,
 * skipping empty values so absent inputs do not overwrite defaults.
 *
 * @param query - The query object mutated in place
 * @param key - The query key to set when the value is present
 * @param value - The raw pagination value, possibly empty
 */
export function assignPositiveParam(
  query: Record<string, string>,
  key: string,
  value: string | number | undefined | null,
): void {
  if (isEmptyParam(value)) return;
  query[key] = String(toPositiveInteger(key, value as string | number));
}

/**
 * buildFilterQuery function.
 *
 * @param {*} filters - Parameter description
 *
 * @returns {*} Return value description
 */
export function buildFilterQuery(filters: any): Record<string, any> {
  const query: Record<string, any> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query[key] = value;
    }
  });
  return query;
}

/**
 * buildQueryParams function.
 *
 * @param {*} params - Parameter description
 *
 * @returns {*} Return value description
 */
export function buildQueryParams(params: SearchParams): Record<string, any> {
  const { pagination = {}, filters = {}, order_by, order, cover } = params;
  const query: Record<string, any> = {
    ...buildPaginationQuery(pagination),
    ...buildFilterQuery(filters),
  };
  // 'default' order (the third click of the tri-state cycle) is expressed by
  // omitting the params entirely, so the backend applies its own default
  // ordering. Only forward a sort when a key is actually selected.
  if (order_by) {
    query.order_by = order_by;
    query.order = order ?? 'asc';
  }
  if (cover) query.cover = cover;
  return query;
}

/**
 * transformSearchResponse function.
 *
 * @param {*} response - Parameter description
 *
 * @returns {*} Return value description
 */
export function transformSearchResponse(response: any): SearchResponse {
  const dataArray = response.data || [];
  const data = arrayToObjectById(dataArray);
  const metadata = {
    pages: response.pages || 1,
    statistics: response.statistics || {},
  };
  return { data, metadata };
}
